import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { QueryState } from "@components/ui/QueryState";
import { useGetEventReportsQuery } from "@api/pApi";
import type { DailyReportOut } from "@api/types";
import { HsePanel } from "./HsePanel";

/**
 * HSE screen — the standalone route. Resolves the current well's active event,
 * lets the user pick a daily report, and renders the HSE panel (event-level LTI
 * rollup + the day's HSE header + the daily HSE trail).
 *
 * Safety is a DAILY HEADER, not a per-observation open→close action list: the
 * screen surfaces LTI counters, the day's well-control / compliance checks, and
 * the daily HSE trail. Every counter is server-computed by Oxbo-P.
 */
export function Hse() {
  const { t } = useLocale();
  const { well, activeEvent, isLoading: ctxLoading, isError: ctxError } = useCurrentWell();

  const eventId = activeEvent?.id ?? "";
  const {
    data: reports = [],
    isLoading,
    isError,
    refetch,
  } = useGetEventReportsQuery(eventId, { skip: !eventId });

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Default to the newest report when the list loads / changes.
  const selected = useMemo<DailyReportOut | undefined>(() => {
    if (selectedId) return reports.find((r) => r.id === selectedId) ?? reports[0];
    return reports[0];
  }, [reports, selectedId]);

  return (
    <div>
      <div className="page-head">
        <h1>{t("hse.title")}</h1>
        <p className="subtitle">
          {well ? `${well.commonName} · ${well.field}` : t("well.notSelected")}
        </p>
      </div>

      <QueryState
        isLoading={ctxLoading || isLoading}
        isError={ctxError || isError}
        isEmpty={!activeEvent}
        emptyMessage={t("well.event.noEvents")}
        onRetry={refetch}
      >
        {activeEvent && reports.length > 0 ? (
          <>
            <div className="ddr-toolbar">
              <div className="field">
                <label htmlFor="hse-report">{t("ddr.selectReport")}</label>
                <select
                  id="hse-report"
                  value={selected?.id ?? ""}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {reports.map((r) => (
                    <option key={r.id} value={r.id}>
                      {t("ddr.day", { n: r.report_no })} · {r.date_report} ·{" "}
                      {t(r.state === "closed" ? "state.closed" : "state.open")}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selected ? <HsePanel eventId={eventId} reportId={selected.id} /> : null}
          </>
        ) : (
          <div className="card">
            <div className="empty-state">{t("hse.noData")}</div>
          </div>
        )}
      </QueryState>
    </div>
  );
}
